import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, updateDoc, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

/**
 * Admin Dashboard สำหรับ Marketplace
 * จัดการ User, Store, Order, Auction
 */
const AdminMarketplaceDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStores: 0,
    totalOrders: 0,
    totalAuctions: 0,
    activeStores: 0,
    pendingOrders: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Load Users (USER, FARM roles)
      const usersQuery = query(
        collection(db, 'users'),
        where('role', 'in', ['user', 'farm'])
      );
      const usersSnapshot = await getDocs(usersQuery);
      const totalUsers = usersSnapshot.size;

      // Load Stores
      const storesQuery = query(collection(db, 'stores'));
      const storesSnapshot = await getDocs(storesQuery);
      const stores = storesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const totalStores = stores.length;
      const activeStores = stores.filter(s => s.status === 'active').length;

      // Load Orders
      const ordersQuery = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(1000));
      const ordersSnapshot = await getDocs(ordersQuery);
      const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const totalOrders = orders.length;
      const pendingOrders = orders.filter(o => o.status === 'pending_payment' || o.status === 'paid').length;

      // Load Auctions
      const auctionsQuery = query(collection(db, 'auctions'), orderBy('createdAt', 'desc'), limit(1000));
      const auctionsSnapshot = await getDocs(auctionsQuery);
      const totalAuctions = auctionsSnapshot.size;

      setStats({
        totalUsers,
        totalStores,
        totalOrders,
        totalAuctions,
        activeStores,
        pendingOrders
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">📊 Marketplace Admin Dashboard</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 ภาพรวม
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            👥 จัดการผู้ใช้
          </button>
          <button
            onClick={() => setActiveTab('stores')}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'stores'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🏪 จัดการร้านค้า
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'orders'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📦 จัดการออเดอร์
          </button>
          <button
            onClick={() => setActiveTab('auctions')}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'auctions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔨 จัดการประมูล
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-2">ผู้ใช้ทั้งหมด</div>
            <div className="text-3xl font-bold text-gray-800">{stats.totalUsers}</div>
            <div className="text-xs text-gray-400 mt-2">USER + FARM</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-2">ร้านค้าทั้งหมด</div>
            <div className="text-3xl font-bold text-gray-800">{stats.totalStores}</div>
            <div className="text-xs text-gray-400 mt-2">ร้านที่เปิด: {stats.activeStores}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-2">ออเดอร์ทั้งหมด</div>
            <div className="text-3xl font-bold text-gray-800">{stats.totalOrders}</div>
            <div className="text-xs text-gray-400 mt-2">รอการดำเนินการ: {stats.pendingOrders}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-2">ประมูลทั้งหมด</div>
            <div className="text-3xl font-bold text-gray-800">{stats.totalAuctions}</div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <AdminUserManagement />
      )}

      {activeTab === 'stores' && (
        <AdminStoreManagement />
      )}

      {activeTab === 'orders' && (
        <AdminOrderManagement />
      )}

      {activeTab === 'auctions' && (
        <AdminAuctionManagement />
      )}
    </div>
  );
};

// User Management Component
const AdminUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, user, farm

  useEffect(() => {
    loadUsers();
  }, [filter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      let q;
      
      if (filter === 'all') {
        q = query(collection(db, 'users'), where('role', 'in', ['user', 'farm']));
      } else {
        q = query(collection(db, 'users'), where('role', '==', filter));
      }
      
      const snapshot = await getDocs(q);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { role: newRole });
      alert('อัพเดท role สำเร็จ');
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      alert('เกิดข้อผิดพลาด');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>;
  }

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          ทั้งหมด
        </button>
        <button
          onClick={() => setFilter('user')}
          className={`px-4 py-2 rounded ${filter === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          USER
        </button>
        <button
          onClick={() => setFilter('farm')}
          className={`px-4 py-2 rounded ${filter === 'farm' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
        >
          FARM
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">เหรียญ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่สร้าง</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จัดการ</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map(user => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.displayName || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.role === 'farm' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role?.toUpperCase() || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.coins || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select
                    value={user.role || 'user'}
                    onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                    className="border rounded px-2 py-1"
                  >
                    <option value="user">USER</option>
                    <option value="farm">FARM</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Store Management Component
const AdminStoreManagement = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'stores'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const storesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStores(storesData);
    } catch (error) {
      console.error('Error loading stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStoreStatus = async (storeId, newStatus) => {
    try {
      const storeRef = doc(db, 'stores', storeId);
      await updateDoc(storeRef, { status: newStatus });
      alert('อัพเดทสถานะสำเร็จ');
      loadStores();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('เกิดข้อผิดพลาด');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อร้าน</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">เจ้าของ</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่สร้าง</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จัดการ</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {stores.map(store => (
            <tr key={store.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{store.name || '-'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{store.ownerId || '-'}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  store.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {store.status || 'pending'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {store.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || '-'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                <select
                  value={store.status || 'pending'}
                  onChange={(e) => handleUpdateStoreStatus(store.id, e.target.value)}
                  className="border rounded px-2 py-1"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Order Management Component
const AdminOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadOrders();
  }, [filter]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      let q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'), limit(100));
      
      if (filter !== 'all') {
        q = query(collection(db, 'orders'), where('status', '==', filter), orderBy('createdAt', 'desc'), limit(100));
      }
      
      const snapshot = await getDocs(q);
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>;
  }

  return (
    <div>
      <div className="mb-4 flex gap-2 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          ทั้งหมด
        </button>
        <button onClick={() => setFilter('pending_payment')} className={`px-4 py-2 rounded ${filter === 'pending_payment' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          รอชำระเงิน
        </button>
        <button onClick={() => setFilter('paid')} className={`px-4 py-2 rounded ${filter === 'paid' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          ชำระเงินแล้ว
        </button>
        <button onClick={() => setFilter('shipping')} className={`px-4 py-2 rounded ${filter === 'shipping' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          กำลังจัดส่ง
        </button>
        <button onClick={() => setFilter('delivered')} className={`px-4 py-2 rounded ${filter === 'delivered' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
          ส่งแล้ว
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ผู้ซื้อ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ยอดรวม</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {orders.map(order => (
              <tr key={order.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.orderNumber || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{order.buyerName || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿{order.total?.toLocaleString() || '0'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                    order.status === 'shipping' ? 'bg-purple-100 text-purple-800' :
                    order.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {order.status || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {order.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Auction Management Component
const AdminAuctionManagement = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuctions();
  }, []);

  const loadAuctions = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'auctions'), orderBy('createdAt', 'desc'), limit(100));
      const snapshot = await getDocs(q);
      const auctionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAuctions(auctionsData);
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อประมูล</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ผู้ขาย</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ราคาเริ่มต้น</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่สร้าง</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {auctions.map(auction => (
            <tr key={auction.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{auction.title || '-'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{auction.sellerId || '-'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">฿{auction.startingPrice?.toLocaleString() || '0'}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 text-xs rounded-full ${
                  auction.status === 'active' ? 'bg-green-100 text-green-800' :
                  auction.status === 'ended' ? 'bg-gray-100 text-gray-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {auction.status || '-'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {auction.createdAt?.toDate?.()?.toLocaleDateString('th-TH') || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminMarketplaceDashboard;








